import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import { useTranslation } from 'react-i18next'

import { Dialog, UploadButton, snackbar } from 'components'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { resizeFile } from 'utils'

import { Tag, UpdateTagResponse, UpdateTagData } from '../../types'
import { updateTag } from '../../api'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  description: [
    {
      validate: (description: string) => description.length <= 200,
      message: 'No puede superar los 200 caracteres',
    },
  ],
}

interface UpdateTagDialogProps {
  handleClose: () => void
  tagToUpdate: Tag
  onDone: (newPrize: Tag) => void
}

const UpdateTagDialog: React.FC<UpdateTagDialogProps> = ({ handleClose, onDone, tagToUpdate }) => {
  const [title, setTitle] = useState(tagToUpdate.title)
  const [description, setDescription] = useState(tagToUpdate.description)
  const [active, setActive] = useState(tagToUpdate.active)
  const [group, setGroup] = useState(tagToUpdate.group || '')
  const [subgroup, setSubGroup] = useState(tagToUpdate.subgroup || null)

  const [iconFile, setIconFile] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState('')
  const [, setNameFile] = useState('')

  const [updateTagApi, isLoading] = useApiCall<UpdateTagData, UpdateTagResponse>(updateTag)
  const { t } = useTranslation()
  // Mostrar icono ya existente si lo tiene
  useEffect(() => {
    if (tagToUpdate.icon) {
      if (typeof tagToUpdate.icon === 'string') {
        setIconPreview(tagToUpdate.icon)
        setNameFile('Imagen existente')
      } else {
        // Si fuera un File (aunque raro al editar), se puede crear una URL también
        const fileUrl = URL.createObjectURL(tagToUpdate.icon)
        setIconPreview(fileUrl)
        setNameFile(tagToUpdate.icon.name)
      }
    }
  }, [tagToUpdate])

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setTitle(e.target.value)
  }

  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setDescription(e.target.value)
  }

  const handleActiveChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setActive(e.target.checked)
  }
  const fields = { title, description, group }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(fields, rules)
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading

  const onUpdateTag = async () => {
    const tagPayload: any = {
      title,
      description,
      active,
      group,
      subgroup: group === 'FILTER' ? subgroup : null, // 👈 forzado siempre
    }

    if (iconFile) {
      // Se subió un nuevo archivo
      tagPayload.icon = iconFile
    } else if (!iconFile && !iconPreview && typeof tagToUpdate.icon === 'string') {
      // Se eliminó la imagen existente
      tagPayload.icon = '' // Valor que el backend interpretará como "borrar"
    }

    const payload = {
      id: tagToUpdate.id,
      payload: tagPayload,
    }

    try {
      const { tag: updatedTag } = await updateTagApi(payload)
      onDone(updatedTag)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo editar la etiqueta(tag). Intente de nuevo.')
    }
  }

  const handleFileChange = async (file: File) => {
    const resizedFile = await resizeFile(file, 192, 192)
    const previewUrl = URL.createObjectURL(resizedFile)
    setIconFile(resizedFile)
    setIconPreview(previewUrl)
    setNameFile(file.name)
  }

  const handleRemoveImage = () => {
    setIconFile(null)
    setIconPreview('')
    setNameFile('')
  }

  return (
    <Dialog
      title="Editar etiqueta(tag)"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateTag}
    >
      <Grid container spacing={3}>
        <Grid item xs={11}>
          <TextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.title.hasError}
            helperText={errors.title.message}
            inputProps={{ maxLength: 25 }}
          />
        </Grid>
        <Grid item xs={11}>
          <TextField
            label="Descripción"
            value={description}
            fullWidth
            onChange={handleDescriptionChange}
            disabled={isLoading}
            required
            error={errors.description.hasError}
            helperText={errors.description.message}
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item xs={11}>
          <FormControl fullWidth required>
            <InputLabel id="type-select-label">Tipo</InputLabel>
            <Select
              labelId="type-select-label"
              value={group}
              onChange={e => {
                const value = e.target.value as string
                setGroup(value)
                if (value !== 'FILTER') setSubGroup(null) // Limpiar si no es FILTER
              }}
              disabled={isLoading}
            >
              <MenuItem value="ALL">{t('tags:ALL')}</MenuItem>
              <MenuItem value="BRANDS">{t('tags:BRANDS')}</MenuItem>
              <MenuItem value="LEVELS">{t('tags:LEVELS')}</MenuItem>
              <MenuItem value="FILTER">{t('tags:FILTER')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {group === 'FILTER' && (
          <Grid item xs={11}>
            <FormControl fullWidth required>
              <InputLabel id="subtype-select-label">Subtipo</InputLabel>
              <Select
                labelId="subtype-select-label"
                value={subgroup}
                onChange={e => setSubGroup(e.target.value as string)}
                disabled={isLoading}
              >
                <MenuItem value="CATEGORY">{t('tags:CATEGORY')}</MenuItem>
                <MenuItem value="DISCOUNT">{t('tags:DISCOUNT')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography>Icono</Typography>
          <UploadButton
            id="icon-upload"
            accept=".jpg, .jpeg, .png"
            label="Subir icon"
            onChange={file => handleFileChange(file)}
          />
          {iconPreview && (
            <>
              <img
                src={iconPreview}
                alt="icon Preview"
                style={{ width: 100, height: 100, display: 'block', marginTop: 10 }}
              />
              <Button color="secondary" onClick={() => handleRemoveImage()}>
                Eliminar
              </Button>
            </>
          )}
        </Grid>

        <Grid item xs={12} sm={12}>
          <FormControlLabel
            control={<Checkbox onChange={handleActiveChange} checked={active} />}
            label="Activo"
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UpdateTagDialog }
